import { isSplitChild } from './splitOrders';

/**
 * @typedef {object} LinkedOrder
 * @property {string} order_id
 * @property {string} [order_number]
 * @property {string|null} [original_order_id] The order this one was split or cloned from.
 * @property {number|null} [split_seq] Set only on split children.
 */

/**
 * @typedef {object} LinkedOrdersTree
 * @property {LinkedOrder[]} path The current order's ancestors from the top down, then the current order. Empty when
 *   the current order isn't in the family.
 * @property {Array<{ order: LinkedOrder, depth: number }>} rows Every order in tree order: each order is followed by
 *   its own descendants before its next sibling. Top-level orders have depth 0.
 */

// Under one parent: split children first in split_seq order (numeric, so -2 precedes -10), then clones by
// order_number.
const compareSiblings = (a, b) => {
    const aSplit = isSplitChild(a);
    const bSplit = isSplitChild(b);
    if (aSplit !== bSplit) return aSplit ? -1 : 1;
    if (aSplit && a.split_seq !== b.split_seq) return a.split_seq - b.split_seq;
    return (a.order_number ?? '').localeCompare(b.order_number ?? '');
};

/**
 * Builds the linked-orders family into a tree from each row's `original_order_id`, for the Order Details breadcrumb
 * and title menu. A row is top-level when its link is null or points outside `orders` (for example an ancestor the
 * viewer can't read). When there are several, the top of the current order's path comes first and the others follow
 * it at the top level. A loop in the links can't repeat or hang: each order appears once.
 *
 * @param {LinkedOrder[]|null|undefined} orders The whole family, in any order.
 * @param {string} [currentOrderId] The order being viewed.
 * @returns {LinkedOrdersTree} The breadcrumb path and the flattened menu rows.
 */
export const buildLinkedOrdersTree = (orders, currentOrderId) => {
    const byId = new Map();
    (orders ?? []).forEach((o) => {
        if (o?.order_id && !byId.has(o.order_id)) byId.set(o.order_id, o);
    });
    if (!byId.size) return { path: [], rows: [] };

    const parentOf = (o) =>
        o.original_order_id && o.original_order_id !== o.order_id ? byId.get(o.original_order_id) : undefined;

    const children = new Map();
    const tops = [];
    byId.forEach((o) => {
        const parent = parentOf(o);
        if (!parent) {
            tops.push(o);
            return;
        }
        if (!children.has(parent.order_id)) children.set(parent.order_id, []);
        children.get(parent.order_id).push(o);
    });

    const path = [];
    const current = byId.get(currentOrderId);
    if (current) {
        const seen = new Set();
        for (let o = current; o && !seen.has(o.order_id); o = parentOf(o)) {
            seen.add(o.order_id);
            path.unshift(o);
        }
    }
    const pathTop = path[0];

    tops.sort(compareSiblings);
    // The current order's top leads. If the path ran into a loop, its top has a parent, so it isn't in tops yet.
    const ordered = pathTop ? [pathTop, ...tops.filter((o) => o !== pathTop)] : tops;

    const rows = [];
    const visited = new Set();
    const visit = (o, depth) => {
        if (visited.has(o.order_id)) return;
        visited.add(o.order_id);
        rows.push({ order: o, depth });
        [...(children.get(o.order_id) ?? [])].sort(compareSiblings).forEach((c) => visit(c, depth + 1));
    };
    ordered.forEach((o) => visit(o, 0));
    // Rows caught in a loop that no top reaches still show, at the top level.
    [...byId.values()]
        .filter((o) => !visited.has(o.order_id))
        .sort(compareSiblings)
        .forEach((o) => visit(o, 0));

    return { path, rows };
};
